import React, { useEffect, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilUser } from '@coreui/icons'

import Notifications from 'src/components/notifications/NotificationsToaster'

import * as cognito from 'src/services/cognito'
import jwt from 'jwt-decode'
import { AuthContext } from 'src/contexts/authContext'
import { GlobalVariablesContext } from 'src/contexts/globalVariableContext'
import { Password, Username } from 'src/components/auth/authComponents'
import { useValidPassword, useValidUsername } from 'src/hooks/useAuthHooks'
import Honeypot from 'src/components/hidden/Honeypot'

const Login = () => {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const authContext = useContext(AuthContext)
  const { username, setUsername, usernameIsValid } = useValidUsername('')
  const { password, setPassword, passwordIsValid } = useValidPassword('')
  const [error, setError] = useState('')
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false)

  const { setForceUsername, setForcePassword } = useContext(GlobalVariablesContext)

  const isValid =
    !usernameIsValid || username.length === 0 || !passwordIsValid || password.length === 0

  const signInClicked = async () => {
    setIsSpinnerVisible(true)
    try {
      const res = await authContext.signInWithEmail(username, password)
      if (res.callback === 'newPasswordRequired') {
        setForceUsername(username)
        setForcePassword(password)
        navigate('/force-new-password')
      } else {
        const session = await cognito.getSession()
        const accessTokenDecoded = jwt(session.idToken.jwtToken)
        if (accessTokenDecoded.language) {
          i18n.changeLanguage(accessTokenDecoded.language.toLowerCase())
        }
        navigate(localStorage.getItem('redirectPath'))
        //navigate('/tender-overview')
      }
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        await authContext.sendCode(username)
        navigate('/verify-code')
      } else {
        setError(err.message)
      }
    } finally {
      setIsSpinnerVisible(false)
      resetForm()
    }
  }

  //reset errors
  const resetForm = () => {
    document.getElementById('auth-login-form').reset()
    setUsername('')
    setPassword('')
  }

  useEffect(() => {
    if (username !== '' || password !== '') setError(false)
  }, [username, password])

  return (
    <div className='bg-body-tertiary min-vh-100 d-flex flex-row align-items-center'>
      {error ? <Notifications type='error' message={error} /> : null}
      {/* Honey */}
      <Honeypot />
      {/* Honey */}
      <CContainer>
        {/* Enable SAML IdP only for production */}
        {process.env.REACT_APP_COGNITO_USER_POOL_ID === 'eu-central-1_Zl5wo1id0' && (
          <CRow className='justify-content-center'>
            <CCol md={4}>
              <CButton
                color='primary'
                href={`https://${process.env.REACT_APP_COGNITO_DOMAIN}.auth.eu-central-1.amazoncognito.com/oauth2/authorize?identity_provider=hgc&redirect_uri=${process.env.REACT_APP_COGNITO_REDIRECT_URI}&response_type=TOKEN&client_id=${process.env.REACT_APP_COGNITO_APP_CLIENT_ID}&scope=aws.cognito.signin.user.admin email openid profile`}
                className='px-4 my-4 w-100'
                tabIndex={-1}
              >
                {t('LoginWithHGC')}
              </CButton>
            </CCol>
          </CRow>
        )}
        <CRow className='justify-content-center'>
          <CCol md={4}>
            <CCardGroup>
              <CCard className='p-4'>
                <CCardBody>
                  <CForm id='auth-login-form'>
                    <h1>{t('Login')}</h1>
                    <p className='text-body-secondary'>{t('LoginText')}</p>
                    <CInputGroup className='mb-3'>
                      <CInputGroupText>
                        <CIcon icon={cilUser} />
                      </CInputGroupText>
                      <Username usernameIsValid={usernameIsValid} setUsername={setUsername} />
                    </CInputGroup>
                    <CInputGroup className='mb-4'>
                      <CInputGroupText>
                        <CIcon icon={cilLockLocked} />
                      </CInputGroupText>
                      <Password isValid={passwordIsValid} setPassword={setPassword} />
                    </CInputGroup>
                    <CContainer className='px-0'>
                      <CRow>
                        <CCol>
                          <CButton
                            disabled={isValid}
                            color='primary'
                            onClick={signInClicked}
                            className='px-4'
                            tabIndex={-1}
                          >
                            {isSpinnerVisible ? (
                              <CSpinner color='light' variant='grow' size='sm' />
                            ) : (
                              t('Login')
                            )}
                          </CButton>
                        </CCol>
                        <CCol className='col-md-auto'>
                          <CButton color='link' href='/forgot-password' className='px-0'>
                            {t('ForgotPassword')}?
                          </CButton>
                        </CCol>
                      </CRow>
                    </CContainer>
                  </CForm>
                </CCardBody>
              </CCard>
            </CCardGroup>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Login
